/**
 * Opções de um item do cardápio — tamanho, sabores, borda, massa, etapas de combo e
 * complementos — no formato `optionGroups` do iFood (Catalog v2), com três modelos:
 *
 *   DEFAULT  item simples, com grupos de complementos opcionais (adicionais, molhos…)
 *   PIZZA    tamanhos (cada um com pedaços e limite de sabores) + sabores com preço por
 *            tamanho + borda/massa opcionais. Meio a meio cobra pelo sabor mais caro
 *            (ou pela média, se o lojista escolher).
 *   COMBO    preço fixo do combo + etapas obrigatórias ("escolha o sabor", "escolha o
 *            refrigerante"); opção com preço soma ao combo.
 *
 * Grupo:  { id, name, type, min, max, priceRule, status, options[] }
 *   type       'SIZE' | 'TOPPING' (sabor) | 'EDGE' (borda) | 'CRUST' (massa) | 'GENERIC'
 *   priceRule  'SUM' (soma tudo) | 'HIGHEST' (maior preço) | 'AVERAGE' (média)
 * Opção:  { id, name, description, price, prices?, slices?, maxFlavors?, status }
 *   prices     { [tamanhoId]: preço } — sabor/borda de pizza; tamanho sem preço = não vende
 *
 * Seleção do cliente: { tamanho: tamanhoId|null, escolhas: { [grupoId]: { [opcaoId]: qtd } } }
 *
 * O cardápio do cliente calcula o preço na tela e no carrinho por aqui, e o pedido grava
 * `selecao` + `options` para que o backend possa refazer a mesma conta a partir do
 * cardápio. Quando o backend passar a conferir o preço, ele deve usar uma cópia CommonJS
 * deste arquivo (Auuii-Backend/src/services/opcoesItem.js), mudando junto com esta.
 */

export const MODELOS = { DEFAULT: 'DEFAULT', PIZZA: 'PIZZA', COMBO: 'COMBO' };
export const TIPOS_GRUPO = { SIZE: 'SIZE', TOPPING: 'TOPPING', EDGE: 'EDGE', CRUST: 'CRUST', GENERIC: 'GENERIC' };
export const REGRAS_PRECO = { SOMA: 'SUM', MAIOR: 'HIGHEST', MEDIA: 'AVERAGE' };

const numero = (v) => {
    if (v && typeof v === 'object' && 'value' in v) v = v.value; // iFood: price { value, originalValue }
    if (v === null || v === undefined || v === '') return null;
    const n = typeof v === 'string' ? parseFloat(v.replace('R$', '').replace(',', '.').trim()) : Number(v);
    return Number.isFinite(n) ? n : null;
};
const inteiro = (v, padrao) => {
    const n = parseInt(v, 10);
    return Number.isFinite(n) && n >= 0 ? n : padrao;
};
const centavos = (v) => Math.round((Number(v) || 0) * 100) / 100;
const disponivel = (x) => x && x.status !== 'UNAVAILABLE';

/** Modelo padrão de uma categoria: pizza → PIZZA, combo → COMBO, o resto → DEFAULT. */
export function modeloDaCategoria(tipo) {
    const t = String(tipo || '').toLowerCase();
    if (/pizza/.test(t)) return MODELOS.PIZZA;
    if (/combo/.test(t)) return MODELOS.COMBO;
    return MODELOS.DEFAULT;
}

/** Modelo de um item já gravado (itens antigos não têm `template`). */
export function modeloDoItem(item = {}) {
    const t = String(item.template || '').toUpperCase();
    if (MODELOS[t]) return t;
    return MODELOS.DEFAULT;
}

let seq = 0;
/** Id curto e estável para grupo/opção criado no editor. */
export function novoId(prefixo = 'op') {
    seq = (seq + 1) % 1e6;
    return `${prefixo}-${Date.now().toString(36)}${seq.toString(36)}`;
}

/** Limpa os grupos vindos do editor ou do banco (números, ids, min ≤ max, sem nome vazio). */
export function normalizarGrupos(grupos) {
    if (!Array.isArray(grupos)) return [];
    return grupos
        .filter((g) => g && String(g.name || '').trim())
        .map((g) => {
            const type = TIPOS_GRUPO[g.type] ? g.type : TIPOS_GRUPO.GENERIC;
            const options = (Array.isArray(g.options) ? g.options : [])
                .filter((o) => o && String(o.name || '').trim())
                .map((o) => {
                    const op = {
                        id: String(o.id || novoId('op')),
                        name: String(o.name).trim(),
                        description: String(o.description || '').trim(),
                        price: numero(o.price) ?? 0,
                        status: o.status === 'UNAVAILABLE' ? 'UNAVAILABLE' : 'AVAILABLE',
                    };
                    if (o.externalCode) op.externalCode = String(o.externalCode);
                    if (o.prices && typeof o.prices === 'object') {
                        op.prices = {};
                        Object.entries(o.prices).forEach(([k, v]) => {
                            const n = numero(v);
                            op.prices[k] = n === null ? null : n;
                        });
                    }
                    if (type === TIPOS_GRUPO.SIZE) {
                        op.slices = inteiro(o.slices, 0);
                        op.maxFlavors = Math.max(1, inteiro(o.maxFlavors, 1));
                    }
                    return op;
                });
            let min = inteiro(g.min, 0);
            let max = inteiro(g.max, type === TIPOS_GRUPO.SIZE ? 1 : Math.max(1, options.length));
            if (type === TIPOS_GRUPO.SIZE) { min = 1; max = 1; }
            if (max < 1) max = 1;
            if (min > max) min = max;
            const priceRule = Object.values(REGRAS_PRECO).includes(g.priceRule)
                ? g.priceRule
                : (type === TIPOS_GRUPO.TOPPING ? REGRAS_PRECO.MAIOR : REGRAS_PRECO.SOMA);
            return {
                id: String(g.id || novoId('grp')),
                name: String(g.name).trim(),
                type,
                min,
                max,
                priceRule,
                status: g.status === 'UNAVAILABLE' ? 'UNAVAILABLE' : 'AVAILABLE',
                options,
            };
        });
}

/** Grupos que o cliente escolhe (o "Tamanho" da marmitex é tratado por `precos`). */
function gruposDeEscolha(item) {
    const grupos = Array.isArray(item.optionGroups) ? item.optionGroups : [];
    return grupos.filter((g) => {
        if (!g || !disponivel(g)) return false;
        if (item.precos && g.id === 'tamanho') return false;
        return true;
    });
}

/** Grupo de tamanhos da pizza (ou null). */
export function grupoDeTamanhos(item = {}) {
    const g = (item.optionGroups || []).find((x) => x?.type === TIPOS_GRUPO.SIZE);
    return g || null;
}

/** Tamanhos vendáveis: pizza (grupo SIZE) ou marmitex (precos P/M/G). */
export function tamanhosDoItem(item = {}) {
    const g = grupoDeTamanhos(item);
    if (g) {
        return g.options.filter(disponivel).map((o) => ({
            id: o.id, name: o.name, price: numero(o.price) ?? 0, slices: o.slices || 0, maxFlavors: o.maxFlavors || 1,
        }));
    }
    if (item.precos && typeof item.precos === 'object') {
        const nomes = { P: 'Pequeno', M: 'Médio', G: 'Grande' };
        return Object.entries(item.precos)
            .map(([k, v]) => ({ id: k, name: nomes[k] || k, price: numero(v) }))
            .filter((t) => t.price !== null && t.price > 0);
    }
    return [];
}

/** Preço de uma opção no tamanho escolhido; null = não vende nesse tamanho. */
export function precoDaOpcao(opcao, tamanhoId) {
    // Com tabela por tamanho, tamanho sem preço preenchido = não vende nesse tamanho
    // (o editor nem grava a chave quando o campo fica vazio).
    if (opcao?.prices && Object.keys(opcao.prices).length && tamanhoId) {
        return numero(opcao.prices[tamanhoId]);
    }
    return numero(opcao?.price) ?? 0;
}

/** Opções de um grupo que valem para o tamanho escolhido. */
export function opcoesNoTamanho(grupo, tamanhoId) {
    return (grupo?.options || []).filter((o) => disponivel(o) && precoDaOpcao(o, tamanhoId) !== null);
}

/** min/max do grupo — sabores de pizza seguem o limite do tamanho escolhido. */
export function limitesDoGrupo(grupo, item, tamanhoId) {
    let { min = 0, max = 1 } = grupo || {};
    if (grupo?.type === TIPOS_GRUPO.TOPPING) {
        const tam = tamanhosDoItem(item).find((t) => t.id === tamanhoId);
        if (tam?.maxFlavors) max = tam.maxFlavors;
        min = Math.max(1, Math.min(min || 1, max));
    }
    return { min, max };
}

/** Quanto um grupo cobra, dada a lista [{ preco, qtd }]. */
function cobrancaDoGrupo(regra, escolhidas) {
    const qtdTotal = escolhidas.reduce((s, e) => s + e.qtd, 0);
    if (!qtdTotal) return 0;
    if (regra === REGRAS_PRECO.MAIOR) return Math.max(...escolhidas.map((e) => e.preco));
    if (regra === REGRAS_PRECO.MEDIA) return escolhidas.reduce((s, e) => s + e.preco * e.qtd, 0) / qtdTotal;
    return escolhidas.reduce((s, e) => s + e.preco * e.qtd, 0);
}

/**
 * Calcula a linha do carrinho/pedido para uma seleção. Não confia em preço vindo do
 * cliente: tudo sai do item. Devolve { ok, erros, unitario, tamanho, opcoes }.
 *   opcoes = [{ groupId, groupName, optionId, name, quantity, price }] — o formato que
 *   o painel e a comanda já leem; `price` é a parte que a opção soma à unidade.
 */
export function calcularLinha(item = {}, selecao = {}) {
    const erros = [];
    const modelo = modeloDoItem(item);
    const tamanhos = tamanhosDoItem(item);
    const escolhas = (selecao && selecao.escolhas) || {};
    let tamanho = null;
    let base = numero(item.price ?? item.preco) ?? 0;

    if (tamanhos.length) {
        tamanho = tamanhos.find((t) => t.id === selecao?.tamanho) || null;
        if (!tamanho) erros.push('Escolha o tamanho');
        else if (item.precos && !grupoDeTamanhos(item)) base = tamanho.price; // marmitex
        else base = (modelo === MODELOS.PIZZA ? 0 : base) + (tamanho.price || 0);
    }
    const tamanhoId = tamanho?.id || null;

    const opcoes = [];
    let adicionais = 0;
    gruposDeEscolha(item).forEach((grupo) => {
        if (grupo.type === TIPOS_GRUPO.SIZE) return;
        const { min, max } = limitesDoGrupo(grupo, item, tamanhoId);
        const validas = opcoesNoTamanho(grupo, tamanhoId);
        const marcadas = escolhas[grupo.id] || {};
        const escolhidas = [];
        Object.entries(marcadas).forEach(([opId, q]) => {
            const qtd = inteiro(q, 0);
            if (!qtd) return;
            const op = validas.find((o) => o.id === opId);
            if (!op) { erros.push(`Opção indisponível em ${grupo.name}`); return; }
            escolhidas.push({ op, qtd, preco: precoDaOpcao(op, tamanhoId) || 0 });
        });
        const qtdTotal = escolhidas.reduce((s, e) => s + e.qtd, 0);
        if (qtdTotal < min) erros.push(min === 1 ? `Falta escolher: ${grupo.name}` : `Escolha pelo menos ${min} em ${grupo.name}`);
        if (qtdTotal > max) erros.push(`Máximo de ${max} em ${grupo.name}`);

        const cobrado = cobrancaDoGrupo(grupo.priceRule, escolhidas);
        adicionais += cobrado;

        const fracionado = grupo.priceRule === REGRAS_PRECO.MAIOR || grupo.priceRule === REGRAS_PRECO.MEDIA;
        let rateado = 0;
        escolhidas.forEach((e, i) => {
            const parte = fracionado && qtdTotal > 1 ? `${e.qtd}/${qtdTotal} ` : '';
            // Fracionado: divide o que o grupo cobrou entre os sabores; o último leva o
            // centavo que sobra do arredondamento, para a soma bater com a unidade.
            let preco = e.preco * e.qtd;
            if (fracionado) {
                preco = i === escolhidas.length - 1
                    ? centavos(cobrado) - rateado
                    : centavos((cobrado * e.qtd) / qtdTotal);
                rateado = centavos(rateado + preco);
            }
            opcoes.push({
                groupId: grupo.id,
                groupName: grupo.name,
                optionId: e.op.id,
                name: `${parte}${e.op.name}`,
                quantity: fracionado ? 1 : e.qtd,
                price: centavos(preco),
            });
        });
    });

    return {
        ok: erros.length === 0,
        erros,
        unitario: centavos(base + adicionais),
        tamanho: tamanho ? { id: tamanho.id, name: tamanho.name } : null,
        opcoes,
    };
}

/** Chave estável da seleção — mesma seleção soma quantidade no carrinho. */
export function chaveDaSelecao(selecao = {}) {
    const partes = [selecao.tamanho || ''];
    Object.keys(selecao.escolhas || {}).sort().forEach((g) => {
        Object.keys(selecao.escolhas[g]).sort().forEach((o) => {
            const q = inteiro(selecao.escolhas[g][o], 0);
            if (q) partes.push(`${g}:${o}x${q}`);
        });
    });
    return partes.join('|');
}

/** O cliente precisa abrir o item para escolher algo antes de adicionar? */
export function precisaEscolher(item = {}) {
    if (tamanhosDoItem(item).length) return true;
    return gruposDeEscolha(item).some((g) => g.type !== TIPOS_GRUPO.SIZE && (g.min || 0) > 0)
        || modeloDoItem(item) !== MODELOS.DEFAULT;
}

/** Tem algo para escolher (obrigatório ou não)? */
export function temOpcoes(item = {}) {
    return tamanhosDoItem(item).length > 0 || gruposDeEscolha(item).some((g) => g.type !== TIPOS_GRUPO.SIZE);
}

/**
 * Menor preço possível ("a partir de"): menor tamanho + o mínimo obrigatório de cada
 * grupo (as opções mais baratas). Pizza sem nenhum sabor cadastrado fica com o tamanho.
 * Com `tamanhoId`, o menor preço dentro daquele tamanho (tela de detalhes).
 */
export function precoAPartirDe(item = {}, tamanhoId = null) {
    const modelo = modeloDoItem(item);
    const tamanhos = tamanhosDoItem(item);
    const base = numero(item.price ?? item.preco) ?? 0;
    const doTamanho = tamanhoId && tamanhos.some((t) => t.id === tamanhoId);
    const candidatos = doTamanho ? [tamanhoId] : (tamanhos.length ? tamanhos.map((t) => t.id) : [null]);
    const precos = candidatos.map((tamId) => {
        const tam = tamanhos.find((t) => t.id === tamId);
        let total = tam
            ? (item.precos && !grupoDeTamanhos(item) ? tam.price : (modelo === MODELOS.PIZZA ? 0 : base) + (tam.price || 0))
            : base;
        gruposDeEscolha(item).forEach((g) => {
            if (g.type === TIPOS_GRUPO.SIZE) return;
            const { min } = limitesDoGrupo(g, item, tamId);
            if (!min) return;
            const valores = opcoesNoTamanho(g, tamId).map((o) => precoDaOpcao(o, tamId) || 0).sort((a, b) => a - b);
            if (!valores.length) return;
            if (g.priceRule === REGRAS_PRECO.SOMA) total += valores.slice(0, min).reduce((s, v) => s + v, 0);
            else total += valores[0];
        });
        return total;
    });
    return centavos(Math.min(...precos));
}

/** Resumo curto para a listagem do painel ("Pequena, Grande · 30 sabores · borda"). */
export function resumoDasOpcoes(item = {}) {
    const grupos = gruposDeEscolha(item);
    if (!grupos.length) return '';
    const modelo = modeloDoItem(item);
    if (modelo === MODELOS.PIZZA) {
        const partes = [];
        const tam = tamanhosDoItem(item).map((t) => t.name).filter(Boolean);
        if (tam.length) partes.push(tam.join(', '));
        const sab = grupos.find((g) => g.type === TIPOS_GRUPO.TOPPING);
        const n = (sab?.options || []).filter(disponivel).length;
        partes.push(n === 1 ? '1 sabor' : `${n} sabores`);
        if (grupos.some((g) => g.type === TIPOS_GRUPO.EDGE)) partes.push('borda');
        if (grupos.some((g) => g.type === TIPOS_GRUPO.CRUST)) partes.push('massa');
        return partes.join(' · ');
    }
    if (modelo === MODELOS.COMBO) return grupos.map((g) => g.name).join(' · ');
    return grupos.length === 1 ? `Complementos: ${grupos[0].name}` : `${grupos.length} grupos de complementos`;
}
